/**
 * GPS Assignment API Service
 * Handles all GPS assignment-related API calls
 */

import axios from 'axios';

const GPS_API_BASE_URL = process.env.REACT_APP_GPS_API_URL || 'https://jmaffrrt2j.execute-api.us-west-2.amazonaws.com/prod';

// Create axios instance for GPS API
const gpsClient = axios.create({
  baseURL: GPS_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for logging
gpsClient.interceptors.request.use(
  (config) => {
    console.log('🚀 GPS API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      data: config.data
    });
    return config;
  },
  (error) => {
    console.error('❌ GPS API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
gpsClient.interceptors.response.use(
  (response) => {
    console.log('✅ GPS API Response:', {
      status: response.status,
      url: response.config.url
    });
    return response;
  },
  (error) => {
    console.error('❌ GPS API Error:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    throw error;
  }
);

/**
 * Assignment Management API
 */
export const assignmentAPI = {
  /**
   * Assign assessment to a leader's team
   */
  assignToTeam: async (assessmentId, leaderEmail, startDate, adhocParticipants = []) => {
    const payload = {
      leaderEmail,
      startDate
    };
    
    // Add ad-hoc participants if provided
    if (adhocParticipants && adhocParticipants.length > 0) {
      payload.adhocParticipants = adhocParticipants;
    }
    
    const response = await gpsClient.post(`/api/assessments/${assessmentId}/assign`, payload);
    return response.data;
  },

  /**
   * Get all assignments for an assessment
   */
  getAssignments: async (assessmentId) => {
    const response = await gpsClient.get(`/api/assessments/${assessmentId}/assignments`);
    return response.data;
  },

  /**
   * Get assignment details
   */
  getAssignmentDetails: async (assignmentId) => {
    const response = await gpsClient.get(`/api/assignments/${assignmentId}`);
    return response.data;
  },

  /**
   * Delete assignment
   */
  deleteAssignment: async (assignmentId) => {
    const response = await gpsClient.delete(`/api/assignments/${assignmentId}`);
    return response.data;
  },

  /**
   * Close assignment (stop pillar rotation)
   */
  closeAssignment: async (assignmentId) => {
    const response = await gpsClient.post(`/api/assignments/${assignmentId}/close`);
    return response.data;
  }
};

/**
 * Team Member API
 */
export const teamMemberAPI = {
  /**
   * Get my assigned assessments
   */
  getMyAssessments: async (userEmail) => {
    const response = await gpsClient.get('/api/my-assessments', {
      params: { email: userEmail }
    });
    return response.data;
  },

  /**
   * Get current active pillar for an assessment
   */
  getCurrentPillar: async (assessmentId, userEmail) => {
    const response = await gpsClient.get(`/api/my-assessments/${assessmentId}/current-pillar`, {
      params: { email: userEmail }
    });
    return response.data;
  },

  /**
   * Submit pillar response
   */
  submitPillarResponse: async (assessmentId, pillarNumber, responses, questionnaireId) => {
    const response = await gpsClient.post(
      `/api/my-assessments/${assessmentId}/pillars/${pillarNumber}/submit`,
      {
        responses,
        questionnaireId
      }
    );
    return response.data;
  }
};

/**
 * Error handling utility
 */
export const handleGPSAPIError = (error) => {
  if (error.response) {
    return {
      type: 'server_error',
      status: error.response.status,
      message: error.response.data?.error || error.response.data?.message || 'Server error occurred',
      details: error.response.data
    };
  } else if (error.request) {
    return {
      type: 'network_error',
      message: 'Network error. Please check your connection.',
      details: error.request
    };
  } else {
    return {
      type: 'unknown_error',
      message: error.message || 'An unexpected error occurred',
      details: error
    };
  }
};

export default {
  assignmentAPI,
  teamMemberAPI,
  handleGPSAPIError
};
